import { HouseCheckRequest } from './public/HouseCheckRequest.tsx';
import { createRoot } from 'react-dom/client';
import { useEffect } from 'react';
import { AuthProvider, useAuth } from './shared/auth.tsx';
import { RouterProvider, useRouter } from './shared/router.tsx';
import { Spinner } from './shared/ui.tsx';
import { ForgotPage, InviteAcceptPage, JUST_SIGNED_IN, LoginPage, MfaPage, PasswordResetPage, ResetLinkPage } from './shared/login.tsx';
import { DemoSwitch } from './shared/DemoSwitch.tsx';
import { RouteDemo } from './field/RouteDemo.tsx';
import { OwnerApp } from './owner/OwnerApp.tsx';
import { OfficeApp } from './office/OfficeApp.tsx';
import { FieldApp } from './field/FieldApp.tsx';
import { Landing } from './shared/Landing.tsx';
import { ComingSoon } from './shared/ComingSoon.tsx';
import { isRmsSite, markProduct } from './shared/site.ts';
markProduct();
import { PortalApp } from './portal/PortalApp.tsx';
import { TransferLink } from './public/TransferLink.tsx';
import { SetOutLink } from './public/SetOutLink.tsx';
import { PageReply } from './public/PageReply.tsx';
import { NewsPage, TipPage } from './public/TipPage.tsx';

function Root() {
  const { me, loading } = useAuth();
  const { path, navigate } = useRouter();

  useEffect(() => {
    if (/^\/(t|s|tip|check|news|reset|invite|page)\//.test(path) || path.startsWith('/owner') || path.startsWith('/forgot')) return; // links and owner console: no redirects
    // Right after signing in, office staff (admins, supervisors, clerks, dispatch) start in the office even if they
    // signed in on the deputy app's page; they can still open the deputy app from the menu.
    let fresh = false;
    try {
      fresh = !!me?.mfaVerified && sessionStorage.getItem(JUST_SIGNED_IN) === '1';
      if (fresh) sessionStorage.removeItem(JUST_SIGNED_IN);
    } catch {
      /* private window */
    }
    // BlueHQ deputies start on their home page; CivilHound deputies on their papers.
    const deputyStart = me?.agency.product === 'civilhound' ? '/field' : '/field/home';
    // Everyone in a BlueHQ agency lands on Home.
    const officeStart = me?.agency.product === 'civilhound' ? '/' : '/home';
    if (fresh && me!.user.role !== 'process_server' && me!.user.role !== 'customer' && (path.startsWith('/field') || path.split('?')[0] === '/')) { if (path !== officeStart) navigate(officeStart, true); }
    else if (fresh && me!.user.role === 'process_server' && path.split('?')[0] === '/field') navigate(deputyStart, true);
    else if (me?.mfaVerified && me.user.role === 'process_server' && !path.startsWith('/field') && !path.startsWith('/route-demo')) navigate(deputyStart, true);
    else if (me?.mfaVerified && me.user.role === 'customer' && path !== '/portal') navigate('/portal', true);
    else if (me?.mfaVerified && path === '/signin') navigate(me.user.role === 'process_server' ? deputyStart : officeStart, true);
  }, [me, path]);

  // Secure link for a sheriff's office not on CivilHound: works signed in or not.
  if (path.startsWith('/t/')) return <TransferLink token={path.slice(3).split(/[?#]/)[0]!} />;
  if (path.startsWith('/tip/')) return <TipPage code={path.slice(5).split(/[?#/]/)[0]!} />;
  if (path.startsWith('/check/')) return <HouseCheckRequest code={path.slice(7).split(/[?#/]/)[0]!} />;
  if (path.startsWith('/news/')) return <NewsPage code={path.slice(6).split(/[?#/]/)[0]!} />;
  if (path.startsWith('/page/')) return <PageReply token={path.slice(6).split(/[?#]/)[0]!} />;
  if (path.startsWith('/s/')) return <SetOutLink token={path.slice(3).split(/[?#]/)[0]!} />;
  // Accounts by email and the owner console: work whether or not someone is signed in to an agency.
  if (path.startsWith('/reset/')) return <ResetLinkPage token={path.slice(7).split(/[?#]/)[0]!} />;
  if (path.startsWith('/invite/')) return <InviteAcceptPage token={path.slice(8).split(/[?#]/)[0]!} />;
  if (path.startsWith('/forgot')) return <ForgotPage />;
  if (path === '/owner' || path.startsWith('/owner/') || path.startsWith('/owner?')) return <OwnerApp />;
  if (loading) return <div className="center"><Spinner /></div>;
  // Public cover page at the main address; the deputy app (/field) and /signin go straight to sign-in.
  if (!me) return path.split(/[?#]/)[0] === '/' ? isRmsSite() ? <ComingSoon /> : <Landing /> : <LoginPage />;
  if (!me.mfaVerified) return <MfaPage />;
  if (me.user.mustResetPassword) return <PasswordResetPage />;
  if (me.user.role === 'customer') return <PortalApp />;
  const demo = me.agency.showroom ? <DemoSwitch /> : null;
  // Demo agencies: addresses and attempts only, full screen, to show the routing.
  if (me.agency.showroom && path.split(/[?#]/)[0] === '/route-demo') return <RouteDemo />;
  if (path.startsWith('/field')) return <>{demo}<FieldApp /></>;
  return <>{demo}<OfficeApp /></>;
}

if ('serviceWorker' in navigator && location.protocol !== 'file:') {
  navigator.serviceWorker.register('/sw.js', { scope: '/' }).catch(() => undefined);
}

createRoot(document.getElementById('root')!).render(
  <RouterProvider>
    <AuthProvider>
      <Root />
    </AuthProvider>
  </RouterProvider>,
);
